"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState, useCallback } from "react";
import BrailleSpinner from "@/app/components/BrailleSpinner";
import NavStockSearch from "./NavStockSearch";
import AuthModal from "./AuthModal";

import WatermarkToggleButton from "./WatermarkToggleButton";
import ConfirmModal from "./ConfirmModal";
import MobileAppsMenu from "./MobileAppsMenu";
import { createClient } from "../../lib/supabase/client";
import { useNavigationReady } from "../contexts/NavigationReadyContext";
import { 
  getEntitlements, 
  readEntitlementsCache, 
  setupEntitlementsBroadcastListener,
  clearEntitlementsCache
} from "../../lib/entitlements";
import { isAuthError } from "../../lib/auth-errors";
import { refreshIfNeeded } from "../../lib/supabase/session-refresh";
import { useAuth } from "../providers/AuthProvider";
import { useAdmin } from "@/lib/hooks/useAdmin";
import { PRODUCT_OVERVIEW_PATH } from "@/lib/hub/public-nav-links";
// Используем тот же BroadcastChannel и кэш, что и в StockPageAuthWrapper
const AUTH_CHANNEL_NAME = 'bastion_auth_sync';
let authChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined') {
  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
  } catch (e) {
    console.warn('BroadcastChannel not supported, falling back to localStorage');
  }
}

const AUTH_CACHE_KEY = 'bastion_auth_cache';
const AUTH_CACHE_TIMESTAMP_KEY = 'bastion_auth_cache_timestamp';
const AUTH_CACHE_TTL = 10 * 60 * 1000; // 10 минут

// Вспомогательная функция для чтения кэша при инициализации (до определения getCachedAuth)
function readInitialCache(): { isActive: boolean; isTrialing: boolean; isPaid: boolean; planName?: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const { readEntitlementsCache } = require('../../lib/entitlements');
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age < AUTH_CACHE_TTL) {
        const user = cached === 'null' ? null : JSON.parse(cached);
        if (user) {
          const entitlementsCache = readEntitlementsCache();
          if (entitlementsCache && entitlementsCache.userId === user.id) {
            return {
              isActive: entitlementsCache.isActive,
              isTrialing: entitlementsCache.isTrialing,
              isPaid: entitlementsCache.isPaid,
              planName: entitlementsCache.planName,
            };
          }
        }
      }
    }
  } catch (e) {
    // Игнорируем ошибки при инициализации
  }
  return null;
}

// Очередь запросов авторизации для TopNav (отдельная от StockPageAuthWrapper)
let topNavAuthRequestQueue: Array<() => Promise<any>> = [];
let isProcessingTopNavAuthQueue = false;
import {
  Radar,
  BarChart4,
  Briefcase,
  Grid3x3,
  Newspaper,
  Binoculars,
  Package,
  CreditCard,
  Settings,
  LogOut,
  Star,
  ChevronDown,
  Crown,
  Flame,
  BookOpen,
  Activity,
} from "lucide-react";

const UserIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const MenuIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"/>
    <line x1="3" y1="12" x2="21" y2="12"/>
    <line x1="3" y1="18" x2="21" y2="18"/>
  </svg>
);

const UserAvatar = ({ user }: { user: any }) => {
  const [profile, setProfile] = useState<any>(null);
  const [mounted, setMounted] = useState(false);
  const runIdRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Загружаем профиль пользователя только на клиенте
  useEffect(() => {
    setMounted(true);
    const runId = ++runIdRef.current;
    
    const fetchProfile = async () => {
      if (!user?.id) return;
      
      // Отменяем предыдущий запрос
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      const controller = new AbortController();
      abortControllerRef.current = controller;
      
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('profiles')
          .select('avatar_url, full_name')
          .eq('id', user.id)
          .single()
          .abortSignal(controller.signal);
        
        // Проверяем, что это еще актуальный запрос
        if (runId !== runIdRef.current || controller.signal.aborted) {
          return;
        }
        
        if (error) {
          if (error.code !== 'PGRST116') {
            console.error('Error fetching profile:', error.message || error.code || error);
          }
        } else {
          setProfile(data);
        }
      } catch (error: any) {
        if (error?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) {
          return;
        }
        console.error('Error fetching profile:', error?.message || error);
      }
    };

    fetchProfile();
    
    return () => {
      runIdRef.current++; // Инвалидируем все будущие обновления
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [user?.id]);

  // Получаем инициалы (две буквы) из имени или email (как в Watchtower комментариях)
  const getInitials = () => {
    if (profile?.full_name) {
      const names = profile.full_name.trim().split(' ');
      if (names.length >= 2) {
        // Если есть имя и фамилия, берем первые буквы каждого
        return (names[0][0] + names[names.length - 1][0]).toUpperCase().slice(0, 2);
      } else {
        // Если только одно слово, берем первые две буквы
        return names[0].slice(0, 2).toUpperCase();
      }
    }
    
    if (user?.user_metadata?.full_name) {
      const names = user.user_metadata.full_name.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase().slice(0, 2);
      } else {
        return names[0].slice(0, 2).toUpperCase();
      }
    }
    
    if (user?.email) {
      // Берем первые две буквы из email (до @)
      return user.email.trim().split('@')[0].slice(0, 2).toUpperCase();
    }
    
    return 'U';
  };

  const initials = getInitials();
  const avatarUrl = profile?.avatar_url;

  // Показываем инициалы по умолчанию (стабильно для SSR) - стиль как на /admin/users
  if (!mounted || !avatarUrl) {
    return (
      <div className="w-8 h-8 rounded-full bg-[#3bd576]/20 flex items-center justify-center text-[#3bd576] text-sm font-semibold">
        {initials}
      </div>
    );
  }

  // Если есть аватар, показываем его (только на клиенте)
  return (
    <div className="w-8 h-8 rounded-full overflow-hidden">
      <img 
        src={avatarUrl} 
        alt="User avatar" 
        className="w-full h-full object-cover"
        onError={(e) => {
          // Если аватар не загрузился, показываем инициалы - стиль как на /admin/users
          const target = e.target as HTMLImageElement;
          target.style.display = 'none';
          const parent = target.parentElement;
          if (parent) {
            parent.innerHTML = `<div class="w-full h-full bg-[#3bd576]/20 flex items-center justify-center text-[#3bd576] text-sm font-semibold">${initials}</div>`;
          }
        }}
      />
    </div>
  );
};

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  const pathname = usePathname();
  const active = pathname === href || (href !== "/" && pathname.startsWith(href));
  return (
    <Link
      href={href}
      className={`tab ${active ? "tab-active" : ""}`}
    >
      {children}
    </Link>
  );
}

/**
 * MoreNavDropdown — выпадающий список «вторичных» секций (Headlines /
 * Watchtower / Watchlist), которые мы не хотим держать в основном ряду
 * чтобы не перегружать его. Триггер ведёт себя как обычный nav-pill
 * (icon + слово + chevron), а само меню стилизовано под HubDropdown,
 * чтобы визуально не появлялся «второй язык» меню.
 */
function MoreNavDropdown({ user, watchlistItemCount, isAdmin }: { user?: any; watchlistItemCount?: number | null; isAdmin?: boolean }) {
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Items appear here when they don't fit in the primary nav. Each
  // hideClass matches the breakpoint at which the same item moves into
  // the inline nav row, so the user always sees every section either
  // inline or in More — never both, never neither.
  const items: Array<{ href: string; label: string; icon: React.ReactNode; desc: string; hideClass?: string }> = [
    { href: "/headlines",  label: "Headlines",  icon: <Newspaper size={16} strokeWidth={1.6} />,  desc: "Top market news",  hideClass: "lg:hidden" },
    { href: "/watchtower", label: "Watchtower", icon: <Binoculars size={16} strokeWidth={1.6} />, desc: "Signals & alerts", hideClass: "min-[1140px]:hidden" },
    ...(user ? [{
      href: "/watchlist",
      label: `Watchlist${watchlistItemCount ? ` (${watchlistItemCount})` : ''}`,
      icon: <Star size={16} strokeWidth={1.6} />,
      desc: "Your saved tickers",
      hideClass: "min-[1260px]:hidden",
    }] : []),
    { href: PRODUCT_OVERVIEW_PATH, label: "Product", icon: <Package size={16} strokeWidth={1.6} />, desc: "Product overview", hideClass: isAdmin ? "min-[1520px]:hidden" : undefined },
    ...(isAdmin ? [{ href: "/trending", label: "Buzz", icon: <Flame size={16} strokeWidth={1.6} />, desc: "Reddit threads & tickers (admin)", hideClass: "min-[1520px]:hidden" }] : []),
    // Admin-only work-in-progress sections. No hideClass: the inline row has no room for them,
    // so they live in More at every width (which is why More stays visible for admins).
    ...(isAdmin ? [
      { href: "/desk", label: "Desk", icon: <Activity size={16} strokeWidth={1.6} />, desc: "Bastion reports built from terminal infographics" },
      { href: "/pulse", label: "Pulse", icon: <BookOpen size={16} strokeWidth={1.6} />, desc: "Daily hot takes from bank research" },
    ] : []),
  ];

  const isActiveAny = items.some(it => it.href === "/" ? pathname === "/" : (pathname === it.href || pathname.startsWith(it.href)));
  const triggerColor = hovered || open ? "#ffffff" : (isActiveAny ? "#ffffff" : "#b0b0b0");

  const handleEnter = () => {
    if (closeTimeoutRef.current) { clearTimeout(closeTimeoutRef.current); closeTimeoutRef.current = null; }
    setHovered(true);
    setOpen(true);
  };
  const handleLeave = () => {
    setHovered(false);
    closeTimeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("touchstart", onClickOutside);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("touchstart", onClickOutside);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <style jsx global>{`
        .topnav-more-menu {
          animation: topnavMoreFadeIn 140ms ease-out;
          transform-origin: top left;
        }
        @keyframes topnavMoreFadeIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
        .topnav-more-item:hover { background: rgba(255,255,255,0.05) !important; }
        .topnav-more-item:hover .topnav-more-icon { color: #ffffff !important; background: rgba(255,255,255,0.08) !important; }
      `}</style>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(v => !v)}
        className={`px-3 py-2 rounded-lg transition-colors duration-200 inline-flex items-center gap-2 hover:bg-[#2a2a2a] ${(open || isActiveAny) ? 'bg-[#2a2a2a]' : ''}`}
        style={{
          cursor: 'pointer',
          color: triggerColor,
          fontSize: 14,
          fontWeight: isActiveAny ? 600 : 500,
          letterSpacing: '0.005em',
          height: 34,
        }}
      >
        <span style={{ lineHeight: 1 }}>More</span>
        <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s ease' }}
          />
        </span>
      </button>
      {open && (
        <div
          role="menu"
          className="topnav-more-menu absolute left-0 top-full z-50"
          style={{
            marginTop: 6,
            width: 248,
            padding: 6,
            background: '#1a1a1a',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.45), 0 2px 6px rgba(0,0,0,0.35)',
          }}
        >
          {items.map(it => {
            const active = it.href === "/" ? pathname === "/" : (pathname === it.href || pathname.startsWith(it.href));
            const linkEl = (
              <Link
                href={it.href}
                onClick={() => setOpen(false)}
                role="menuitem"
                className="topnav-more-item"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 7,
                  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  textDecoration: 'none',
                  background: active ? 'rgba(255,255,255,0.05)' : 'transparent',
                  transition: 'background 120ms ease',
                }}
              >
                <span
                  className="topnav-more-icon"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    background: 'rgba(255,255,255,0.04)',
                    color: active ? '#ffffff' : '#9aa0a6',
                    flexShrink: 0,
                    transition: 'color 120ms ease, background 120ms ease',
                  }}
                >
                  {it.icon}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.25 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: active ? '#ffffff' : '#e6e6e6',
                      letterSpacing: '0.005em',
                    }}
                  >
                    {it.label}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: '#7a7a7a',
                      marginTop: 2,
                      letterSpacing: '0.005em',
                    }}
                  >
                    {it.desc}
                  </span>
                </span>
              </Link>
            );
            return it.hideClass
              ? <div key={it.href} className={it.hideClass}>{linkEl}</div>
              : <div key={it.href}>{linkEl}</div>;
          })}
        </div>
      )}
    </div>
  );
}

// Top-nav buttons used to intercept clicks for unauthenticated users and
// pop an AuthModal — that was double-protection against pages that already
// enforce paywalls server-side (/radar, /portfolios, /heatmap) or
// authenticate at the destination (/watchlist). It hurt conversion because
// the user couldn't even see what they were missing. We now let every
// click go through to its destination; the destination owns its
// anon-state UI (teaser, paywall, in-page sign-in modal, etc.).
function IconNavLink({ href, label, icon, iconOnly }: { href: string; label: string; icon: React.ReactNode; iconOnly?: boolean }) {
  const pathname = usePathname();
  const active =
    pathname === href ||
    (href !== "/" && pathname.startsWith(href));
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const color = hovered || active ? '#ffffff' : '#b0b0b0';
  const watchdogRef = useRef<number | null>(null);

  // A committed navigation (pathname change) cancels a pending fallback so
  // the watchdog only fires when the soft nav genuinely no-op'd.
  useEffect(() => {
    if (watchdogRef.current !== null) {
      window.clearTimeout(watchdogRef.current);
      watchdogRef.current = null;
    }
  }, [pathname]);

  useEffect(() => () => {
    if (watchdogRef.current !== null) window.clearTimeout(watchdogRef.current);
  }, []);

  // After a long tab sleep the App Router client can stop handling Link
  // clicks (href still shows on hover, but the soft navigation silently
  // no-ops). Mirror the Hub-logo recovery: if we're STILL on the origin
  // path a few seconds after the click, hard-navigate as a fallback.
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (pathname === href) return; // <Link> no-ops on the current path
    const fromPath = window.location.pathname;
    if (watchdogRef.current !== null) window.clearTimeout(watchdogRef.current);
    watchdogRef.current = window.setTimeout(() => {
      watchdogRef.current = null;
      if (window.location.pathname === fromPath) {
        window.location.assign(href);
      }
    }, 3000);
  };

  return (
    <Link
      href={href}
      aria-label={label}
      className={`relative ${iconOnly ? 'p-2' : 'px-3 py-2'} rounded-lg transition-colors duration-200 inline-flex items-center ${iconOnly ? 'justify-center' : 'gap-2'} hover:bg-[#2a2a2a] ${active ? 'bg-[#2a2a2a]' : ''}`}
      style={{
        cursor: 'pointer',
        color,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.005em',
        height: 34,
      }}
      onClick={handleClick}
      onMouseEnter={() => { setHovered(true); if (iconOnly) setShowTooltip(true); }}
      onMouseLeave={() => { setHovered(false); if (iconOnly) setShowTooltip(false); }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>{icon}</span>
      {!iconOnly && <span style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>{label}</span>}
      {iconOnly && (
        <span
          className="absolute left-1/2 mt-2 px-2.5 py-1 rounded-md text-[12px] font-medium whitespace-nowrap pointer-events-none z-50"
          style={{
            top: '100%',
            transform: 'translateX(-50%)',
            background: '#333',
            color: '#eee',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            opacity: showTooltip ? 1 : 0,
            visibility: showTooltip ? 'visible' : 'hidden',
            transition: 'opacity 120ms ease',
          }}
        >
          {label}
        </span>
      )}
    </Link>
  );
}

const getHubIcon = (href: string) => {
  switch (href) {
    case "/radar":
      return <Radar size={16} strokeWidth={1.5} />;
    case "/stocks":
      return <BarChart4 size={16} strokeWidth={1.5} />;
    case "/heatmap":
      return <Grid3x3 size={16} strokeWidth={1.5} />;
    case "/portfolios":
      return <Briefcase size={16} strokeWidth={1.5} />;
    case "/headlines":
      return <Newspaper size={16} strokeWidth={1.5} />;
    case "/watchtower":
      return <Binoculars size={16} strokeWidth={1.5} />;
    case "/watchlist":
      return <Star size={16} strokeWidth={1.5} />;
    case "/subscription":
      return <CreditCard size={16} strokeWidth={1.5} />;
    default:
      return null;
  }
};

function HubDropdown({
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  user,
  onSignInClick,
  onSignUpClick,
  onNavAuthRequired,
}: {
  open: boolean;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  user?: any;
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  onNavAuthRequired?: (href: string) => void;
}) {
  const router = useRouter();
  const ref = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  useEffect(() => {
    if (!mounted) return;
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, mounted]);

  // Close on click/touch outside
  useEffect(() => {
    if (!open || !mounted) return;
    function handleClickOutside(e: MouseEvent | TouchEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open, onClose, mounted]);
  
  if (!open || !mounted) return null;

  const hubItems = [
    { href: "/radar", label: "Radar", color: "#42db7b", group: "premium" },
    { href: "/portfolios", label: "Portfolios", color: "#42db7b", group: "premium" },
    { href: "/heatmap", label: "Heatmap", color: "#42db7b", group: "pro" },
    { href: "/headlines", label: "Headlines", color: "#42db7b", group: "other" },
    { href: "/watchtower", label: "Watchtower", color: "#42db7b", group: "other" },
    { href: "/watchlist", label: "General Watchlist", color: "#ccae16", group: "personal" },
    { href: "/subscription", label: "Subscription", color: "#FF69B4", group: "subscription" },
  ];
  
  return (
    <div
      ref={ref}
      className="absolute left-0 top-full w-36 rounded-lg border border-[#3d3d3d] bg-[#2a2a2a] shadow-lg z-50"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        marginTop: "2px", // Уменьшаем отступ сверху
        paddingTop: "2px", // Уменьшаем padding сверху
        paddingBottom: "2px", // Уменьшаем padding снизу
        paddingLeft: "2px",
        paddingRight: "2px",
      }}
    >
      {hubItems.map((item, index) => {
        const showDivider = index > 0 && hubItems[index - 1].group !== item.group;
        const HUB_FREE_ROUTES = new Set(['/', '/hub', '/about', '/landing', '/subscription', '/watchtower', '/scrolls']);
        const needsAuth = !user && !HUB_FREE_ROUTES.has(item.href);
        
        if (needsAuth) {
          return (
            <div key={item.href}>
              {showDivider && (
                <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
              )}
              <button
                onClick={() => {
                  onClose();
                  if (onNavAuthRequired) {
                    onNavAuthRequired(item.href);
                  } else if (onSignInClick) {
                    onSignInClick();
                  }
                }}
                className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] transition-all duration-200"
              >
                <div className="flex items-center gap-2 font-medium text-[14px]" style={{
                  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  color: item.color
                }}>
                  <span style={{ color: item.color, display: 'flex', alignItems: 'center' }}>
                    {getHubIcon(item.href)}
                  </span>
                  {item.label}
                </div>
              </button>
            </div>
          );
        }
        
        return (
          <div key={item.href}>
            {showDivider && (
              <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
            )}
            <Link 
              href={item.href}
              className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] transition-all duration-200 cursor-pointer"
              onClick={onClose}
            >
              <div className="flex items-center gap-2 font-medium text-[14px]" style={{
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                color: item.color
              }}>
                <span style={{ color: item.color, display: 'flex', alignItems: 'center' }}>
                  {getHubIcon(item.href)}
                </span>
                {item.label}
              </div>
            </Link>
          </div>
        );
      })}
    </div>
  );
}

function MobileMenuDropdown({
  open,
  onClose,
  user,
  onMouseEnter,
  onMouseLeave,
  onSignInClick,
  onSignUpClick,
  onNavAuthRequired,
  subscriptionStatus,
}: {
  open: boolean;
  onClose: () => void;
  user?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSignInClick?: () => void;
  onSignUpClick?: () => void;
  onNavAuthRequired?: (href: string) => void;
  subscriptionStatus?: { isActive: boolean; isTrialing: boolean } | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  
  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);
  
  if (!open) return null;
  // Use the same structure and style as HubDropdown (exclude Hub itself)
  const hubLikeItems = [
    { href: "/radar", label: "Radar", color: "#42db7b", group: "premium" },
    { href: "/portfolios", label: "Portfolios", color: "#42db7b", group: "premium" },
    { href: "/heatmap", label: "Heatmap", color: "#42db7b", group: "pro" },
    { href: "/headlines", label: "Headlines", color: "#42db7b", group: "other" },
    { href: "/watchtower", label: "Watchtower", color: "#42db7b", group: "other" },
    { href: "/watchlist", label: "General Watchlist", color: "#ccae16", group: "personal" },
  ];
  
  return (
    <div
      ref={ref}
      className="absolute left-0 top-full w-36 rounded-lg border border-[#3d3d3d] bg-[#2a2a2a] shadow-lg z-50"
      style={{
        marginTop: "2px",
        paddingTop: "2px",
        paddingBottom: "2px",
        paddingLeft: "2px",
        paddingRight: "2px",
      }}
      {...(onMouseEnter && onMouseLeave ? { onMouseEnter, onMouseLeave } : {})}
    >
      {hubLikeItems.map((item, index) => {
        const showDivider = index > 0 && hubLikeItems[index - 1].group !== item.group;
        const HUB_FREE_ROUTES = new Set(['/', '/hub', '/about', '/landing', '/subscription', '/watchtower', '/scrolls']);
        const needsAuth = !user && !HUB_FREE_ROUTES.has(item.href);
        
        if (needsAuth) {
          return (
            <div key={item.href}>
              {showDivider && (
                <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
              )}
              <button
                onClick={() => {
                  onClose();
                  if (onNavAuthRequired) {
                    onNavAuthRequired(item.href);
                  } else if (onSignInClick) {
                    onSignInClick();
                  }
                }}
                className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] transition-all duration-200"
              >
                <div className="flex items-center gap-2 font-medium text-[14px]" style={{
                  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  color: item.color
                }}>
                  <span style={{ color: item.color, display: 'flex', alignItems: 'center' }}>
                    {getHubIcon(item.href)}
                  </span>
                  {item.label}
                </div>
              </button>
            </div>
          );
        }
        
        return (
          <div key={item.href}>
            {showDivider && (
              <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
            )}
            <Link 
              href={item.href}
              className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] transition-all duration-200 cursor-pointer"
              onClick={onClose}
            >
              <div className="flex items-center gap-2 font-medium text-[14px]" style={{
                fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                color: item.color
              }}>
                <span style={{ color: item.color, display: 'flex', alignItems: 'center' }}>
                  {getHubIcon(item.href)}
                </span>
                {item.label}
              </div>
            </Link>
          </div>
        );
      })}
      {/* Keep Subscription at the bottom as a special action */}
      <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
      {user ? (
        <Link
          href="/subscription"
          className="block w-full text-left px-2 py-1.5 rounded-md transition-all duration-200 font-medium text-[14px] cursor-pointer"
          style={{
            fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            background: 'linear-gradient(90deg, #7A00FF, #E000FF)',
            color: '#ffffff'
          }}
          onClick={onClose}
        >
          <div className="flex items-center gap-2">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {getHubIcon("/subscription")}
            </span>
            Subscription
          </div>
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => {
            onClose();
            if (onSignUpClick) onSignUpClick();
          }}
          className="block w-full text-left px-2 py-1.5 rounded-md transition-all duration-200 font-medium text-[14px] cursor-pointer"
          style={{
            fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            background: 'linear-gradient(90deg, #7A00FF, #E000FF)',
            color: '#ffffff'
          }}
        >
          <div className="flex items-center gap-2">
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {getHubIcon("/subscription")}
            </span>
            Get Started
          </div>
        </button>
      )}
    </div>
  );
}

function Dropdown({
  open,
  onClose,
  onSignIn,
  onSignUp,
  user,
  onMouseEnter,
  onMouseLeave,
  onSignOutClick,
  watchlistItemCount,
}: {
  open: boolean;
  onClose: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
  user?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSignOutClick?: () => void;
  watchlistItemCount?: number | null;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [profile, setProfile] = useState<any>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Загружаем профиль пользователя
  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) return;

      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', user.id)
          .single();

        if (error) {
          if (error.code !== 'PGRST116') {
            console.error('Error fetching profile:', error.message || error.code || error);
          }
        } else {
          setProfile(data);
        }
      } catch (error: any) {
        console.error('Error fetching profile:', error?.message || error);
      }
    };

    if (user?.id) {
      fetchProfile();
    }
  }, [user?.id]);

  useEffect(() => {
    if (!mounted) return;
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    if (open) {
      document.addEventListener("keydown", onEsc);
    }
    return () => {
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose, mounted]);

  if (!open || !mounted) return null;

  // Build a clean display-name / email pair without showing the same
  // string twice. Previously the second line was hardcoded to
  // `user.email`, so users without a `profiles.full_name` (e.g. signed
  // up via email-only flow) saw the same email rendered on both rows.
  // We resolve a name from the available metadata; if nothing
  // human-readable is available we fall back to just the email and
  // suppress the duplicate secondary row.
  const displayName: string | null =
    profile?.full_name?.trim?.() ||
    user?.user_metadata?.full_name?.trim?.() ||
    null;
  const displayEmail: string | null = user?.email ?? null;
  const primaryLine = displayName || displayEmail || "";
  const secondaryLine = displayName && displayEmail && displayName !== displayEmail
    ? displayEmail
    : null;

  return (
    <div
      ref={ref}
      className="absolute right-0 top-full w-64 rounded-lg border border-[#3d3d3d] bg-[#2a2a2a] shadow-lg z-50"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        marginTop: "2px",
        paddingTop: "2px",
        paddingBottom: "2px",
        paddingLeft: "2px",
        paddingRight: "2px",
        cursor: 'pointer',
      }}
    >
      {user ? (
        <>
          <div className="px-2 py-1.5 border-b border-[#3d3d3d]">
            <div
              className="text-[13px] text-[#dbdbdb] font-medium overflow-hidden text-ellipsis whitespace-nowrap"
              title={primaryLine}
            >
              {primaryLine}
            </div>
            {secondaryLine && (
              <div
                className="text-[11px] text-[#8c8c8c] overflow-hidden text-ellipsis whitespace-nowrap"
                title={secondaryLine}
              >
                {secondaryLine}
              </div>
            )}
          </div>

          <Link
            href="/watchlist"
            className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] text-[#dbdbdb] hover:text-white transition-all duration-200 font-medium text-[14px]"
            style={{
              fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            }}
            onClick={onClose}
          >
            <div className="flex items-center gap-2">
              <Star size={16} strokeWidth={1.5} />
              Watchlist{watchlistItemCount ? ` (${watchlistItemCount})` : ''}
            </div>
          </Link>
          <Link
            href="/settings"
            className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] text-[#dbdbdb] hover:text-white transition-all duration-200 font-medium text-[14px]"
            style={{
              fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
            onClick={onClose}
          >
            <div className="flex items-center gap-2">
              <Settings size={16} strokeWidth={1.5} />
              My Settings
            </div>
          </Link>
          <div className="h-px bg-[#3d3d3d] mx-2 my-1"></div>
          <button
            onClick={() => {
              if (onSignOutClick) {
                onSignOutClick();
              }
              onClose();
            }}
            className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] transition-all duration-200 font-medium text-[14px] cursor-pointer"
            style={{
              fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              color: '#ef4444',
              cursor: 'pointer'
            }}
          >
            <div className="flex items-center gap-2">
              <LogOut size={16} strokeWidth={1.5} />
              Sign out
            </div>
          </button>
        </>
      ) : (
        <button 
          onClick={() => {
            onSignIn();
            onClose();
          }}
          className="block w-full text-left px-2 py-1.5 rounded-md hover:bg-[#1a1a1a] text-[#3bd576] hover:text-white transition-all duration-200 font-medium text-[14px] cursor-pointer"
          style={{
            fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            cursor: 'pointer'
          }}
        >
          Sign in
        </button>
      )}
    </div>
  );
}

export default function TopNav() {
  const pathname = usePathname();
  const router = useRouter();
  const { setNavigationReady } = useNavigationReady();
  const { user: authUser, authLoading } = useAuth(); // Используем единый AuthProvider
  const isAdmin = useAdmin();
  const [open, setOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'register'>('register');
  const [user, setUser] = useState<any>(null); // Локальный state для совместимости, синхронизируем с authUser
  const [loading, setLoading] = useState(false); // Больше не управляем auth loading, только subscription loading
  const [searchFocused, setSearchFocused] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [watchlistItemCount, setWatchlistItemCount] = useState<number | null>(null);
  const [watchlistBump, setWatchlistBump] = useState(false);
  const pendingNavRef = useRef<string | null>(null);
  const hubLogoWatchdogRef = useRef<number | null>(null);
  const userMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userMenuContainerRef = useRef<HTMLDivElement>(null);
  const wasPaidRef = useRef(false); // "Липкий premium" - запоминаем, был ли пользователь premium
  const subscriptionUpdateTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const subscriptionRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null); // отложенный рефетч для гифт-подписки
  const lastSubscriptionStatusRef = useRef<{ isActive: boolean; isTrialing: boolean; planName?: string } | null>(null);
  const lastRefreshAtRef = useRef<number>(0);
  const REFRESH_THROTTLE_MS = 30000; // 30 секунд

  const [subscriptionStatus, setSubscriptionStatus] = useState<{ isActive: boolean; isTrialing: boolean; planName?: string } | null>(null);

  // Restore subscription status from cache after mount to avoid hydration mismatch
  useEffect(() => {
    const cached = readInitialCache();
    if (cached) {
      wasPaidRef.current = cached.isPaid;
      setSubscriptionStatus({
        isActive: cached.isActive,
        isTrialing: cached.isTrialing,
        planName: cached.planName,
      });
    }
  }, []);
  
  // Detect touch device to disable hover-based menu opening (prevents accidental opens on scroll)
  const isTouchDevice = typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);

  const handleUserMenuEnter = () => {
    if (isTouchDevice) return;
    if (userMenuTimeoutRef.current) {
      clearTimeout(userMenuTimeoutRef.current);
      userMenuTimeoutRef.current = null;
    }
    setOpen(true);
  };
  
  const handleUserMenuLeave = () => {
    if (isTouchDevice) return;
    userMenuTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  };
  
  // Close dropdowns on click/touch outside (critical for touch devices)
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent | TouchEvent) {
      if (open && userMenuContainerRef.current && !userMenuContainerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [open]);

  const handleGetStartedClick = async () => {
    if (user) {
      router.push('/subscription');
    } else {
      setAuthMode('register');
      setAuthModalOpen(true);
    }
  };

  const handleNavAuthRequired = useCallback((href: string) => {
    pendingNavRef.current = href;
    setAuthMode('register');
    setAuthModalOpen(true);
  }, []);

  // Ensure component is mounted before showing dynamic content
  useEffect(() => {
    setMounted(true);
  }, []);

  // ?signup=1 is handled by HubSignupGate so guests never see Hub first.

  // Helper функция для безопасной установки subscriptionStatus с sticky premium
  const safeSetSubscriptionStatus = useCallback((status: { isActive: boolean; isTrialing: boolean; planName?: string } | null, fromConfirmedSource = false) => {
    // Если пользователь был premium, не понижаем до free без подтвержденного ответа
    // НО: если это подтвержденный источник (API ответ), обновляем статус даже если был premium
    if (wasPaidRef.current && status && !status.isActive && !status.isTrialing && !fromConfirmedSource) {
      console.log('[TopNav] Sticky premium: keeping paid status, ignoring downgrade to free (unconfirmed)');
      return; // Не обновляем статус, оставляем premium
    }
    
    const last = lastSubscriptionStatusRef.current;
    if (last && status && 
        last.isActive === status.isActive && 
        last.isTrialing === status.isTrialing &&
        last.planName === status.planName) {
      return;
    }
    
    // Обновляем ref при установке premium
    if (status && (status.isActive || status.isTrialing)) {
      wasPaidRef.current = true;
    } else if (status && !status.isActive && !status.isTrialing && fromConfirmedSource) {
      // Если это подтвержденный источник и подписка не активна, сбрасываем sticky premium
      wasPaidRef.current = false;
      console.log('[TopNav] Confirmed source: subscription expired, resetting sticky premium');
    }
    
    // Дебаунсим обновление UI
    if (subscriptionUpdateTimeoutRef.current) {
      clearTimeout(subscriptionUpdateTimeoutRef.current);
    }
    
    subscriptionUpdateTimeoutRef.current = setTimeout(() => {
      setSubscriptionStatus(status);
      if (status) {
        lastSubscriptionStatusRef.current = {
          isActive: status.isActive,
          isTrialing: status.isTrialing,
          planName: status.planName,
        };
      } else {
        lastSubscriptionStatusRef.current = null;
      }
    }, 100); // 100ms дебаунс
  }, []);

  // Инициализируем wasPaidRef из кэша при монтировании (если еще не инициализирован)
  useEffect(() => {
    if (typeof window !== 'undefined' && !wasPaidRef.current) {
      const cached = getCachedAuth();
      if (cached?.user) {
        const entitlementsCache = readEntitlementsCache();
        if (entitlementsCache && entitlementsCache.userId === cached.user.id) {
          wasPaidRef.current = entitlementsCache.isPaid;
        }
      }
    }
    // Обновляем lastSubscriptionStatusRef при монтировании
    if (subscriptionStatus) {
      lastSubscriptionStatusRef.current = subscriptionStatus;
    }
  }, []); // Только при монтировании

  // Инициализируем subscriptionLoading из entitlements cache при изменении user
  useEffect(() => {
    if (user && typeof window !== 'undefined') {
      const entitlementsCache = readEntitlementsCache();
      if (entitlementsCache && entitlementsCache.userId === user.id) {
        safeSetSubscriptionStatus({
          isActive: entitlementsCache.isActive,
          isTrialing: entitlementsCache.isTrialing,
          planName: entitlementsCache.planName,
        }, false); // Кэш - не подтвержденный источник
        if (entitlementsCache.isPaid) {
          wasPaidRef.current = true;
        }
        setSubscriptionLoading(false);
      }
    }
  }, [user?.id, safeSetSubscriptionStatus]);

  // Функция для получения кэшированного состояния авторизации
  const getCachedAuth = (): { user: any; timestamp: number } | null => {
    if (typeof window === 'undefined') return null;
    
    try {
      const cached = localStorage.getItem(AUTH_CACHE_KEY);
      const timestamp = localStorage.getItem(AUTH_CACHE_TIMESTAMP_KEY);
      
      if (cached && timestamp) {
        const age = Date.now() - parseInt(timestamp);
        if (age < AUTH_CACHE_TTL) {
          return {
            user: cached === 'null' ? null : JSON.parse(cached),
            timestamp: parseInt(timestamp)
          };
        }
      }
    } catch (e) {
      console.warn('TopNav: Error reading auth cache:', e);
    }
    
    return null;
  };

  // Функция для сохранения состояния авторизации в кэш
  const setCachedAuth = (user: any, forceClear = false) => {
    if (typeof window === 'undefined') return;
    
    try {
      // Если forceClear=true, это явный sign out - очищаем кэш
      if (forceClear || !user) {
        localStorage.removeItem(AUTH_CACHE_KEY);
        localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
        // НЕ отправляем AUTH_UPDATE с null - это может вызвать ложные logout на других вкладках
        // Supabase сам синхронизирует SIGNED_OUT через storage events
        return;
      }
      
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, Date.now().toString());
      
      // Отправляем сообщение другим вкладкам через BroadcastChannel только при обновлении пользователя
      if (authChannel) {
        authChannel.postMessage({
          type: 'AUTH_UPDATE',
          user: { id: user.id, email: user.email },
          timestamp: Date.now()
        });
      }
    } catch (e) {
      console.warn('TopNav: Error setting auth cache:', e);
    }
  };

  // Обработчик сообщений от других вкладок
  useEffect(() => {
    if (!authChannel) return;
    
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'AUTH_UPDATE') {
        // Если другая вкладка обновила авторизацию, обновляем кэш
        const { user: userData, timestamp, forceRefresh } = event.data;
        if (typeof window !== 'undefined') {
          try {
            if (forceRefresh) {
              // Принудительное обновление - очищаем кэш и загружаем заново
              localStorage.removeItem(AUTH_CACHE_KEY);
              localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
              
              // Загружаем свежие данные пользователя
              const supabase = createClient();
              supabase.auth.getUser().then(({ data: { user } }) => {
                if (user) {
                  setCachedAuth(user);
                  setUser(user);
                }
                // НЕ делаем setCachedAuth(null) здесь - это может быть временный null
                // Кэш очищается только при явном SIGNED_OUT событии
              });
              return;
            }
            
            // Обновляем кэш из localStorage (там полный объект пользователя)
            const cached = localStorage.getItem(AUTH_CACHE_KEY);
            if (cached) {
              localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, timestamp.toString());
              // Если пользователь изменился, обновляем состояние
              if (userData && cached !== 'null') {
                try {
                  const cachedUser = JSON.parse(cached);
                  if (cachedUser?.id === userData.id) {
                    setUser(cachedUser);
                  }
                } catch (e) {
                  // Игнорируем ошибки парсинга
                }
              } else if (!userData && cached === 'null') {
                setUser(null);
              }
            }
          } catch (e) {
            console.warn('TopNav: Error updating auth cache from broadcast:', e);
          }
        }
      }
    };
    
    authChannel.addEventListener('message', handleMessage);
    return () => {
      authChannel?.removeEventListener('message', handleMessage);
    };
  }, []);

  // Функция для выполнения запроса авторизации с задержкой (очередь)
  const executeAuthRequest = async (): Promise<{ user: any; error: any }> => {
    return new Promise((resolve) => {
      const requestFn = async () => {
        try {
          const supabase = createClient();
          
          // Сначала пробуем получить сессию (быстрее, чем getUser)
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          
          // Если auth error при получении сессии - возвращаем error
          if (sessionError && isAuthError(sessionError)) {
            return { user: null, error: sessionError };
          }
          
          if (session?.user && !sessionError) {
            return { user: session.user, error: null };
          }
          
          // Если сессии нет, пробуем getUser с таймаутом
          const getUserPromise = supabase.auth.getUser();
          const timeoutPromise = new Promise<{ data: { user: any }, error: any }>((resolve) => {
            setTimeout(() => {
              resolve({ data: { user: null }, error: { message: 'Timeout' } });
            }, 2000); // Уменьшили таймаут до 2 секунд
          });
          
          const result = await Promise.race([getUserPromise, timeoutPromise]);
          
          // Проверяем, является ли error auth error
          if (result.error && isAuthError(result.error)) {
            return { user: null, error: result.error };
          }
          
          return { user: result.data?.user || null, error: result.error || null };
        } catch (error) {
          return { user: null, error: error as any };
        }
      };
      
      topNavAuthRequestQueue.push(async () => {
        const result = await requestFn();
        resolve(result);
      });
      
      // Обрабатываем очередь последовательно
      if (!isProcessingTopNavAuthQueue) {
        isProcessingTopNavAuthQueue = true;
        const processQueue = async () => {
          while (topNavAuthRequestQueue.length > 0) {
            const nextRequest = topNavAuthRequestQueue.shift();
            if (nextRequest) {
              await nextRequest();
              // Небольшая задержка между запросами для предотвращения конфликтов
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
          isProcessingTopNavAuthQueue = false;
        };
        processQueue();
      }
    });
  };

  // NavigationReady должен устанавливаться сразу при монтировании, независимо от авторизации
  // Это отдельное состояние: "layout готов" != "пользователь проверен"
  useEffect(() => {
    setNavigationReady(true);
  }, [setNavigationReady]);

  // Синхронизируем user из AuthProvider
  useEffect(() => {
    setUser(authUser);
  }, [authUser]);

  const fetchWatchlistCount = useCallback(async () => {
    if (!user?.id) return;
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('watchlists')
        .select('watchlist_items(ticker)')
        .eq('user_id', user.id);
      if (!error && data) {
        const unique = new Set<string>();
        for (const w of data as any[]) {
          for (const item of w.watchlist_items ?? []) {
            if (item.ticker) unique.add(item.ticker);
          }
        }
        setWatchlistItemCount(unique.size);
      }
    } catch {}
  }, [user?.id]);

  // Fetch on mount / user change
  useEffect(() => {
    if (!user?.id) { setWatchlistItemCount(null); return; }
    fetchWatchlistCount();
  }, [user?.id, fetchWatchlistCount]);

  // Re-fetch + animate on watchlist changes from other components
  useEffect(() => {
    const handler = () => {
      fetchWatchlistCount().then(() => {
        setWatchlistBump(true);
        setTimeout(() => setWatchlistBump(false), 600);
      });
    };
    window.addEventListener('watchlist-changed', handler);
    return () => window.removeEventListener('watchlist-changed', handler);
  }, [fetchWatchlistCount]);

  // Check subscription status (auth теперь управляется AuthProvider)
  useEffect(() => {
    let cancelled = false;

    // НЕ сбрасываем статус при authLoading - используем sticky premium
    if (!user) {
      // Только при реальном logout сбрасываем
      wasPaidRef.current = false;
      lastSubscriptionStatusRef.current = null;
      setSubscriptionLoading(false);
      return;
    }

    // Если authLoading, не трогаем subscriptionStatus - оставляем как есть
    if (authLoading) {
      return;
    }

    // Load subscription status using entitlements cache (unified system)
    const loadSubscription = async () => {
      if (cancelled || !user) return;

      // Читаем из entitlements cache мгновенно
      const entitlementsCache = readEntitlementsCache();
      if (entitlementsCache && entitlementsCache.userId === user.id) {
        safeSetSubscriptionStatus({
          isActive: entitlementsCache.isActive,
          isTrialing: entitlementsCache.isTrialing,
          planName: entitlementsCache.planName,
        }, false); // Кэш - не подтвержденный источник
        if (entitlementsCache.isPaid) {
          wasPaidRef.current = true;
        }
        setSubscriptionLoading(false);
      } else {
        // Если кэша нет, показываем loading и обновляем в фоне
        setSubscriptionLoading(true);
        getEntitlements(user.id, false).then((result) => {
          if (cancelled) return;
          safeSetSubscriptionStatus({
            isActive: result.isActive,
            isTrialing: result.isTrialing,
            planName: result.planName,
          }, true); // Подтвержденный источник
          if (result.isPaid) {
            wasPaidRef.current = true;
          }
          setSubscriptionLoading(false);
          // Гифт/подписка могут активироваться чуть позже (create-profile после OAuth). Повторный запрос через 2с.
          if (!result.isPaid) {
            if (subscriptionRetryTimeoutRef.current) clearTimeout(subscriptionRetryTimeoutRef.current);
            subscriptionRetryTimeoutRef.current = setTimeout(() => {
              if (cancelled) return;
              clearEntitlementsCache();
              getEntitlements(user.id, false).then((retryResult) => {
                if (cancelled) return;
                if (retryResult.isPaid) {
                  safeSetSubscriptionStatus({
                    isActive: retryResult.isActive,
                    isTrialing: retryResult.isTrialing,
                    planName: retryResult.planName,
                  }, true);
                  wasPaidRef.current = true;
                }
              }).catch(() => {});
            }, 2000);
          }
        }).catch((error) => {
          if (cancelled) return;
          console.error('Error loading entitlements:', error);
          // При ошибке используем кэш, НЕ сбрасываем в null
          const fallbackCache = readEntitlementsCache();
          if (fallbackCache && fallbackCache.userId === user.id) {
            safeSetSubscriptionStatus({
              isActive: fallbackCache.isActive,
              isTrialing: fallbackCache.isTrialing,
              planName: fallbackCache.planName,
            }, false);
            if (fallbackCache.isPaid) {
              wasPaidRef.current = true;
            }
          }
          setSubscriptionLoading(false);
        });
      }
    };

    loadSubscription();

    // Если прочитали из кэша и там free — делаем отложенный рефетч (гифт мог только что активироваться)
    const entitlementsCacheForRetry = readEntitlementsCache();
    if (entitlementsCacheForRetry && entitlementsCacheForRetry.userId === user.id && !entitlementsCacheForRetry.isPaid) {
      if (subscriptionRetryTimeoutRef.current) clearTimeout(subscriptionRetryTimeoutRef.current);
      subscriptionRetryTimeoutRef.current = setTimeout(() => {
        if (cancelled) return;
        clearEntitlementsCache();
        getEntitlements(user.id, false).then((result) => {
          if (cancelled) return;
          if (result.isPaid) {
            safeSetSubscriptionStatus({
              isActive: result.isActive,
              isTrialing: result.isTrialing,
              planName: result.planName,
            }, true);
            wasPaidRef.current = true;
          }
        }).catch(() => {});
      }, 2000);
    }

    // Check and send welcome email if needed (fallback mechanism)
    // УЛУЧШЕННЫЙ FALLBACK: проверяем для всех пользователей с email
    // Endpoint /api/email/check-and-send-welcome сам проверит условия (email_confirmed_at, is_verified)
    // ВАЖНО: sessionStorage помечается ТОЛЬКО при успешной отправке или alreadySent,
    // чтобы при skip (is_verified ещё false, email не подтверждён) повторить на следующей загрузке страницы
    if (user && !cancelled && user.email) {
      const welcomeCheckKey = `welcome_email_checked_${user.id}`;
      const welcomeChecked = typeof window !== 'undefined' ? sessionStorage.getItem(welcomeCheckKey) : null;
      if (!welcomeChecked) {
        fetch('/api/email/check-and-send-welcome')
          .then(res => res.json())
          .then(data => {
            if (typeof window !== 'undefined') {
              if (data.success && !data.skip) {
                // Email отправлен или уже был отправлен — помечаем, повторять не нужно
                sessionStorage.setItem(welcomeCheckKey, 'true');
                console.log('[TopNav] Welcome email check completed:', data.message);
              } else if (data.alreadySent) {
                // Уже отправлен ранее — помечаем
                sessionStorage.setItem(welcomeCheckKey, 'true');
                console.log('[TopNav] Welcome email already sent:', data.message);
              } else if (data.skip) {
                // Условия не выполнены (is_verified=false, email не подтверждён и т.д.)
                // НЕ помечаем — повторим при следующей загрузке страницы
                console.log('[TopNav] Welcome email skipped (will retry):', data.message);
              } else {
                // Ошибка — НЕ помечаем, повторим
                console.warn('[TopNav] Welcome email check failed (will retry):', data.message || data.error);
              }
            }
          })
          .catch(err => {
            console.warn('[TopNav] Welcome email check failed (will retry):', err);
            // Не сохраняем в sessionStorage при ошибке, чтобы можно было повторить
          });
      }
    }

    return () => {
      cancelled = true;
      if (subscriptionRetryTimeoutRef.current) {
        clearTimeout(subscriptionRetryTimeoutRef.current);
        subscriptionRetryTimeoutRef.current = null;
      }
    };
  }, [user?.id, authLoading, safeSetSubscriptionStatus]);

  // Настройка BroadcastChannel для синхронизации entitlements между вкладками
  useEffect(() => {
    const cleanupEntitlementsListener = setupEntitlementsBroadcastListener((cache) => {
      if (!user || cache.userId !== user.id) return;

      // Проверяем timestamp кэша - игнорируем устаревшие обновления
      const currentCache = readEntitlementsCache();
      if (currentCache && currentCache.timestamp > cache.timestamp) {
        return;
      }

      const last = lastSubscriptionStatusRef.current;
      const newStatus = {
        isActive: cache.isActive,
        isTrialing: cache.isTrialing,
        planName: cache.planName,
      };
      if (last && 
          last.isActive === newStatus.isActive && 
          last.isTrialing === newStatus.isTrialing &&
          last.planName === newStatus.planName) {
        return;
      }

      safeSetSubscriptionStatus(newStatus, true); // Broadcast - считаем подтвержденным
      if (cache.isPaid) {
        wasPaidRef.current = true;
      }
      setSubscriptionLoading(false);
    });
    
    return () => {
      cleanupEntitlementsListener();
      // Очищаем таймаут при размонтировании
      if (subscriptionUpdateTimeoutRef.current) {
        clearTimeout(subscriptionUpdateTimeoutRef.current);
      }
    };
  }, [user?.id, safeSetSubscriptionStatus]);

  // Revalidate on visibility/online: при возврате во вкладку обновляем сессию с throttle
  useEffect(() => {
    let cancelled = false;
    
    const handleRefresh = async () => {
      if (cancelled) return;
      
      const now = Date.now();
      
      // Cross-tab throttle через localStorage (чтобы только одна вкладка делала refresh)
      const lastRefreshLock = parseInt(localStorage.getItem('session_refresh_lock') || '0');
      if (now - lastRefreshLock < REFRESH_THROTTLE_MS) {
        return; // Слишком рано, другая вкладка уже делала refresh
      }
      
      // Локальный throttle (дополнительная защита)
      if (now - lastRefreshAtRef.current < REFRESH_THROTTLE_MS) {
        return; // Слишком рано для этой вкладки
      }
      
      try {
        // Устанавливаем lock для других вкладок
        localStorage.setItem('session_refresh_lock', now.toString());
        lastRefreshAtRef.current = now;
        
        const supabase = createClient();
        // Обновляем сессию если нужно
        await refreshIfNeeded(supabase);
      } catch (error) {
        console.error('Error revalidating session:', error);
      }
    };

    const handleVisibilityChange = () => {
      if (!cancelled && document.visibilityState === 'visible') {
        handleRefresh();
      }
    };

    const handleOnline = () => {
      if (!cancelled) {
        handleRefresh();
      }
    };

    // Убрали обработчик focus (как рекомендовал ChatGPT)
    // Оставляем только visibilitychange и online
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // After a long tab sleep the App Router client can stop handling Link clicks
  // (href still shows on hover, but the soft navigation silently no-ops).
  const handleHubLogoClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    const href = '/hub';

    // Already on /hub: the <Link> would no-op, so scroll to top + refresh.
    if (pathname === href) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      router.refresh();
      return;
    }

    // Let <Link> perform its normal client-side navigation. We deliberately
    // do NOT preventDefault + router.push here: doing our own push on top of
    // Link's, paired with a short hard-reload fallback, raced with the soft
    // navigation. Leaving a heavy page (e.g. /radar) the App Router commits
    // the new URL only after its RSC payload is ready, which routinely takes
    // longer than the old 400ms deadline — so the fallback fired a full
    // window.location.assign on top of an already-working soft nav, loading
    // /hub twice.
    //
    // Keep only a long watchdog for the rare "router stopped handling clicks
    // after a tab sleep" case: if we're STILL on the origin path well after
    // the click, the soft nav truly no-op'd — hard-navigate as a recovery.
    const fromPath = window.location.pathname;
    if (hubLogoWatchdogRef.current !== null) {
      window.clearTimeout(hubLogoWatchdogRef.current);
    }
    hubLogoWatchdogRef.current = window.setTimeout(() => {
      hubLogoWatchdogRef.current = null;
      if (window.location.pathname === fromPath) {
        window.location.assign(href);
      }
    }, 3000);
  }, [pathname, router]);

  // Any committed navigation cancels a pending Hub-logo fallback. Without this,
  // a stale watchdog (captured fromPath = the page you started on) misfires when
  // you bounce away and back: e.g. /heatmap → click Hub → /hub → quickly click
  // Heatmap → back on /heatmap === fromPath, so the 3s timer wrongly assigns
  // /hub. A real soft-nav always changes `pathname`, so clearing here keeps the
  // genuine "router stopped handling clicks" recovery intact (pathname unchanged).
  useEffect(() => {
    if (hubLogoWatchdogRef.current !== null) {
      window.clearTimeout(hubLogoWatchdogRef.current);
      hubLogoWatchdogRef.current = null;
    }
  }, [pathname]);

  return (
    <header className="topnav">
      <div className="topnav-inner">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Mobile: apps hamburger. Desktop: Bastion logo → Hub. */}
        <MobileAppsMenu />
        <div className="hidden md:block shrink-0">
          <Link
            href="/hub"
            aria-label="Hub"
            className="topnav-logo-btn flex items-center justify-center cursor-pointer"
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              border: `1px solid ${pathname === '/hub' ? 'rgba(59,213,118,0.3)' : 'rgba(255,255,255,0.08)'}`,
              background: '#1a1a1a',
              transition: 'all 150ms ease',
            }}
            onClick={handleHubLogoClick}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(59,213,118,0.35)'; }}
            onMouseLeave={(e) => {
              const isActive = pathname === '/hub';
              e.currentTarget.style.borderColor = isActive ? 'rgba(59,213,118,0.3)' : 'rgba(255,255,255,0.08)';
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.93)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <img src="/iconb-mark.png" alt="Bastion" style={{ width: 22, height: 22 }} />
          </Link>
        </div>
        {/* Search — full width on mobile, constrained on desktop */}
        <div
          className={`flex-1 min-w-0 ml-1 md:ml-2 topnav-search${searchFocused ? ' is-focused' : ''}`}
        >
          <NavStockSearch
            compact
            onFocus={() => setSearchFocused(true)}
            onBlur={() => {
              setTimeout(() => setSearchFocused(false), 150);
            }}
          />
        </div>

        {/* Nav links (icon + label) — visible from md (768px), hidden on mobile.
            Headlines + Watchtower live in the primary row on wide screens
            (xl: ≥1280px); below that they collapse into the `More` dropdown.
            Vertical-divider pixels between search/Radar and around the
            About button have been removed — the natural button spacing
            already gives the row enough rhythm without paint-quality
            dividers, and they were the most obvious source of the
            "header looks fussy" feedback. */}
        <div className={`hidden md:flex items-center gap-2 transition-all duration-300 ${searchFocused ? 'opacity-0 pointer-events-none max-w-0 overflow-hidden' : 'opacity-100 max-w-[960px]'}`}>
          <IconNavLink href="/radar" label="Radar" icon={<Radar size={16} strokeWidth={1.75} />} />
          <IconNavLink href="/portfolios" label="Portfolios" icon={<Briefcase size={16} strokeWidth={1.75} />} />
          <IconNavLink href="/heatmap" label="Heatmap" icon={<Grid3x3 size={16} strokeWidth={1.75} />} />
          {/* Items appear one-by-one as viewport widens so the nav
              collapses gracefully instead of losing 3 items at once.
              Headlines → lg (1024), Watchtower → 1140, Watchlist → 1260, About → xl (1280). */}
          <span className="hidden lg:inline-flex">
            <IconNavLink href="/headlines" label="Headlines" icon={<Newspaper size={16} strokeWidth={1.75} />} />
          </span>
          <span className="hidden min-[1140px]:inline-flex">
            <IconNavLink href="/watchtower" label="Watchtower" icon={<Binoculars size={16} strokeWidth={1.75} />} />
          </span>
          {user && (
            <span className="hidden min-[1260px]:inline-flex" style={{ position: 'relative' }}>
              <span style={{ animation: watchlistBump ? 'wlBump 400ms ease' : 'none' }}>
                <IconNavLink
                  href="/watchlist"
                  label={`Watchlist${watchlistItemCount ? ` (${watchlistItemCount})` : ''}`}
                  icon={<Star size={16} strokeWidth={1.75} />}
                />
              </span>
              {watchlistBump && (
                <span style={{
                  position: 'absolute', top: -2, right: -4,
                  fontSize: 11, fontWeight: 700, color: '#DCE2EC',
                  animation: 'wlPlusOne 600ms ease forwards',
                  pointerEvents: 'none',
                }}>+1</span>
              )}
            </span>
          )}
          <span className={isAdmin ? "hidden min-[1520px]:inline-flex" : "hidden min-[1260px]:inline-flex"}>
            <IconNavLink href={PRODUCT_OVERVIEW_PATH} label="Product" icon={<Package size={16} strokeWidth={1.75} />} />
          </span>
          {/* Admin-only: Reddit Buzz (/trending). The 8-item admin row + More only fits (with margin) from 1520px, so
              below that Product + Buzz sit in More. Admins always keep More for Desk + Pulse. */}
          {isAdmin && (
            <span className="hidden min-[1520px]:inline-flex">
              <IconNavLink href="/trending" label="Buzz" icon={<Flame size={16} strokeWidth={1.75} />} />
            </span>
          )}
          <span className={isAdmin ? "" : "min-[1260px]:hidden"}>
            <MoreNavDropdown user={user} watchlistItemCount={watchlistItemCount} isAdmin={isAdmin} />
          </span>
        </div>
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-2 relative">
        {/* Mobile burger removed from right; now located on the left */}

        {(!mounted || loading || authLoading) ? (
          <div className="p-2 rounded-full" suppressHydrationWarning>
            <BrailleSpinner label="" size={22} />
          </div>
        ) : (authUser || user) ? (
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <WatermarkToggleButton className="!px-2 !py-1 !text-xs" />
            </div>
            <div
              ref={userMenuContainerRef}
              className="relative"
              onMouseEnter={handleUserMenuEnter}
              onMouseLeave={handleUserMenuLeave}
              style={{ cursor: 'pointer' }}
            >
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
                className="p-2 rounded-full hover:bg-[#2a2a2a] text-[#8c8c8c] hover:text-[#dbdbdb] transition-all duration-200"
                style={{ cursor: 'pointer' }}
              >
                <UserAvatar user={user} />
              </button>
              <Dropdown 
                open={open} 
                onClose={() => setOpen(false)}
                onSignIn={() => {}}
                onSignUp={() => {}}
                user={user}
                onMouseEnter={handleUserMenuEnter}
                onMouseLeave={handleUserMenuLeave}
                onSignOutClick={() => setShowSignOutConfirm(true)}
                watchlistItemCount={watchlistItemCount}
              />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <div className="hidden sm:block">
              <WatermarkToggleButton className="!px-2 !py-1 !text-xs" />
            </div>
            <button
              type="button"
              onClick={() => {
                setAuthMode('login');
                setAuthModalOpen(true);
              }}
              className="p-2 rounded-full hover:bg-[#2a2a2a] text-[#8c8c8c] hover:text-[#dbdbdb] transition-all duration-200"
              style={{ cursor: 'pointer' }}
            >
              <UserIcon />
            </button>
          </div>
        )}

        {(!mounted || loading || authLoading) ? (
          <div
            className="hidden sm:inline-flex items-center h-7 px-4 rounded-full"
            suppressHydrationWarning
          >
            <div className="relative">
              <BrailleSpinner label="" size={22} />
            </div>
          </div>
        ) : (pathname === '/landing' || pathname === '/about') ? (
          <button
            type="button"
            onClick={handleGetStartedClick}
            className="hidden sm:inline-flex items-center h-7 px-4 rounded-full text-[14px] font-semibold bg-gradient-to-r from-[#42db7b] to-[#3bc56b] text-black hover:brightness-110 transition-all duration-200 cursor-pointer"
            style={{
              fontFamily:
                'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            }}
          >
            Get Started
          </button>
        ) : (authUser || user) ? (
          (() => {
            const isPaidUser = Boolean(
              subscriptionStatus?.isActive ||
              subscriptionStatus?.isTrialing ||
              wasPaidRef.current,
            );

            if (isPaidUser) {
              return (
                <Link
                  href="/subscription"
                  title={subscriptionStatus?.planName || 'Premium'}
                  aria-label={subscriptionStatus?.planName || 'Premium'}
                  className="hidden sm:inline-flex rounded-full shrink-0 text-[#c9a227] hover:text-[#dbb84a] transition-all duration-200"
                >
                  <span
                    className="w-8 h-8 rounded-full inline-flex items-center justify-center"
                    style={{
                      background: 'rgba(201, 162, 39, 0.08)',
                      boxShadow: '0 0 0 1px rgba(201, 162, 39, 0.18), 0 0 8px rgba(201, 162, 39, 0.16)',
                    }}
                  >
                    <Crown size={18} strokeWidth={1.75} />
                  </span>
                </Link>
              );
            }

            return (
              <Link
                href="/subscription"
                className={`hidden sm:inline-flex items-center h-7 px-4 rounded-full text-[16px] font-semibold hover:brightness-110 transition-all duration-200 ${
                  subscriptionLoading && !subscriptionStatus
                    ? 'opacity-50'
                    : 'bg-gradient-to-r from-[#7A00FF] to-[#E000FF]'
                }`}
              >
                {subscriptionLoading && !subscriptionStatus ? '' : 'Upgrade'}
              </Link>
            );
          })()
        ) : (
          <button
            type="button"
            onClick={() => { setAuthMode('register'); setAuthModalOpen(true); }}
            className="hidden sm:inline-flex items-center h-7 px-4 rounded-full text-[16px] font-semibold hover:brightness-110 transition-all duration-200 cursor-pointer bg-gradient-to-r from-[#7A00FF] to-[#E000FF]"
            style={{
              fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
            }}
          >
            Get Started
          </button>
        )}
      </div>
      </div>

      {/* Auth Modal */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
          pendingNavRef.current = null;
        }}
        mode={authMode}
        onModeChange={setAuthMode}
        onAuthSuccess={async () => {
          const pendingNav = pendingNavRef.current;
          pendingNavRef.current = null;
          
          try {
            const supabase = createClient();
            const { data: { user } } = await supabase.auth.getUser();
            
            if (user) {
              setUser(user);
              setCachedAuth(user);
              
              if (typeof window !== 'undefined') {
                localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user));
                localStorage.setItem(AUTH_CACHE_TIMESTAMP_KEY, Date.now().toString());
                
                if (authChannel) {
                  authChannel.postMessage({
                    type: 'AUTH_UPDATE',
                    user: { id: user.id, email: user.email },
                    timestamp: Date.now()
                  });
                }
              }
              
              setSubscriptionLoading(true);
              const result = await getEntitlements(user.id, false);
              safeSetSubscriptionStatus({
                isActive: result.isActive,
                isTrialing: result.isTrialing,
                planName: result.planName,
              }, true);
              if (result.isPaid) wasPaidRef.current = true;
              setSubscriptionLoading(false);

              if (pendingNav) {
                window.location.href = pendingNav;
                return;
              }
            }
          } catch (error) {
            console.error('Error updating user state after sign in:', error);
          }
          
          setAuthModalOpen(false);
        }}
        customTitle="Sign in"
        customDescription="Sign in to your account"
      />

      {/* Sign Out Confirmation Modal */}
      <ConfirmModal
        isOpen={showSignOutConfirm}
        title="Sign out?"
        message="Are you sure you want to sign out?"
        confirmText="Sign out"
        cancelText="Cancel"
        onConfirm={async () => {
          try {
            const supabase = createClient();
            // Выполняем signOut
            await supabase.auth.signOut();
            
            // Очищаем состояние сразу
            setUser(null);
            // При реальном logout сбрасываем все
            wasPaidRef.current = false;
            lastSubscriptionStatusRef.current = null;
            setSubscriptionStatus(null);
            setShowSignOutConfirm(false);
            
            // Очищаем кэш авторизации
            if (typeof window !== 'undefined') {
              localStorage.removeItem(AUTH_CACHE_KEY);
              localStorage.removeItem(AUTH_CACHE_TIMESTAMP_KEY);
              
              // Отправляем сообщение другим вкладкам
              if (authChannel) {
                authChannel.postMessage({
                  type: 'AUTH_UPDATE',
                  user: null,
                  timestamp: Date.now()
                });
              }
            }
            
            // Перенаправляем на главную после выхода
            setTimeout(() => {
              window.location.href = '/';
            }, 300);
          } catch (error) {
            console.error('Error signing out:', error);
            setShowSignOutConfirm(false);
            
            // Очищаем состояние даже при ошибке
            setUser(null);
            // При реальном logout сбрасываем все
            wasPaidRef.current = false;
            lastSubscriptionStatusRef.current = null;
            setSubscriptionStatus(null);
            
            // Перенаправляем на главную даже при ошибке
            setTimeout(() => {
              window.location.href = '/';
            }, 300);
          }
        }}
        onCancel={() => setShowSignOutConfirm(false)}
      />
    </header>
  );
}
